import { useState, useEffect } from 'react';
import { api, ClickFunnelsIntegration } from '../lib/api';
import { Plus, Trash2, RefreshCw, CheckCircle, XCircle, ExternalLink, HelpCircle, Wifi, KeyRound, ChevronDown, ChevronUp } from 'lucide-react';

interface TestResultDetails {
  loginUrl: string;
  initialUrl: string;
  loginPageLoaded: boolean;
  passwordFieldVisible: boolean;
  formFound: boolean;
  loginSubmitted: boolean;
  postLoginUrl: string;
  postLoginTitle?: string;
  filledEmail?: string;
  filledPasswordLength?: number;
  pageErrors: string[];
}

interface TestResult {
  type: 'api' | 'credentials';
  success: boolean;
  error?: string;
  details?: TestResultDetails;
}

interface Props {
  userPlan: string;
}

export default function ClickFunnelsIntegrationManager({ userPlan }: Props) {
  const [integrations, setIntegrations] = useState<ClickFunnelsIntegration[]>([]);
  const [limit, setLimit] = useState(1);
  const [canAdd, setCanAdd] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testingType, setTestingType] = useState<'api' | 'credentials' | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult[]>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    api_key: '',
    workspace: '',
    customer_center_url: '',
    scrape_email: '',
    scrape_password: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, []);

  async function loadIntegrations() {
    try {
      setLoading(true);
      const data = await api.getClickFunnelsIntegrations();
      setIntegrations(data.integrations);
      setLimit(data.limit);
      setCanAdd(data.canAdd);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingId) {
        // Strip empty strings for sensitive fields so they don't overwrite stored values
        const updateData: Record<string, string> = {};
        for (const [key, value] of Object.entries(formData)) {
          if (value !== '' || (key !== 'api_key' && key !== 'scrape_password')) {
            updateData[key] = value;
          }
        }
        await api.updateClickFunnelsIntegration(editingId, updateData);
      } else {
        await api.createClickFunnelsIntegration(formData);
      }
      await loadIntegrations();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save integration');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this connection?')) return;

    try {
      await api.deleteClickFunnelsIntegration(id);
      await loadIntegrations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  }

  async function handleTestApi(id: string) {
    setTestingId(id);
    setTestingType('api');
    setError('');

    try {
      const result = await api.testClickFunnelsConnection(id);
      setTestResults(prev => ({
        ...prev,
        [id]: [
          ...(prev[id] || []).filter(r => r.type !== 'api'),
          { type: 'api', success: result.success, error: result.error },
        ],
      }));
      if (!result.success) {
        setError(result.error || 'API connection test failed');
      }
      await loadIntegrations();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'API connection test failed';
      setTestResults(prev => ({
        ...prev,
        [id]: [
          ...(prev[id] || []).filter(r => r.type !== 'api'),
          { type: 'api', success: false, error: msg },
        ],
      }));
      setError(msg);
    } finally {
      setTestingId(null);
      setTestingType(null);
    }
  }

  async function handleTestCredentials(id: string) {
    setTestingId(id);
    setTestingType('credentials');
    setError('');

    try {
      const result = await api.testClickFunnelsCredentials(id);
      setTestResults(prev => ({
        ...prev,
        [id]: [
          ...(prev[id] || []).filter(r => r.type !== 'credentials'),
          { type: 'credentials', success: result.success, error: result.error, details: result.details },
        ],
      }));
      if (!result.success) {
        setError(result.error || 'Credential test failed');
      }
      setExpandedId(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Credential test failed';
      setTestResults(prev => ({
        ...prev,
        [id]: [
          ...(prev[id] || []).filter(r => r.type !== 'credentials'),
          { type: 'credentials', success: false, error: msg },
        ],
      }));
      setError(msg);
    } finally {
      setTestingId(null);
      setTestingType(null);
    }
  }

  function resetForm() {
    setFormData({ name: '', api_key: '', workspace: '', customer_center_url: '', scrape_email: '', scrape_password: '' });
    setEditingId(null);
    setShowForm(false);
  }

  function startEdit(integration: ClickFunnelsIntegration) {
    setFormData({
      name: integration.name,
      api_key: '', // Don't pre-fill API key for security
      workspace: integration.workspace,
      customer_center_url: integration.customer_center_url || '',
      scrape_email: integration.scrape_email || '',
      scrape_password: '', // Don't pre-fill password for security
    });
    setEditingId(integration.id);
    setShowForm(true);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Course Platform Connections</h2>
          <p className="text-sm text-gray-500">
            {integrations.length} of {limit} connections used ({userPlan} plan)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          {canAdd && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add ClickFunnels
            </button>
          )}
        </div>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">How to get your ClickFunnels API Key</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>Log in to your ClickFunnels account</li>
            <li>Go to Settings &rarr; API Access</li>
            <li>Click "Create New Token"</li>
            <li>Give it a name and copy the generated API key</li>
            <li>Your workspace is the subdomain (e.g., "mycompany" from mycompany.myclickfunnels.com)</li>
          </ol>
          <a
            href="https://developers.myclickfunnels.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-700"
          >
            ClickFunnels API Documentation
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Connection Form */}
      {showForm && (
        <div className="bg-blue-900 border border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <img src="https://www.google.com/s2/favicons?domain=myclickfunnels.com&sz=32" alt="" className="w-5 h-5" />
            {editingId ? 'Edit ClickFunnels Connection' : 'New ClickFunnels Connection'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Connection Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My ClickFunnels Account"
                className="w-full px-3 py-2 border border-blue-700 rounded-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                API Key
              </label>
              <input
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder={editingId ? 'Leave blank to keep current' : 'Enter your API key'}
                className="w-full px-3 py-2 border border-blue-700 rounded-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                required={!editingId}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Workspace (subdomain)
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={formData.workspace}
                  onChange={(e) => setFormData({ ...formData, workspace: e.target.value })}
                  placeholder="mycompany"
                  className="flex-1 px-3 py-2 border border-blue-700 rounded-l-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  required
                />
                <span className="px-3 py-2 bg-blue-950 border border-l-0 border-blue-700 rounded-r-lg text-blue-300 text-sm">
                  .myclickfunnels.com
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Customer Center URL
              </label>
              <input
                type="url"
                value={formData.customer_center_url}
                onChange={(e) => setFormData({ ...formData, customer_center_url: e.target.value })}
                placeholder="https://courses.mycompany.com"
                className="w-full px-3 py-2 border border-blue-700 rounded-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                required
              />
              <p className="mt-1 text-xs text-blue-300">
                The URL where your courses are hosted (used for generating lesson URLs)
              </p>
            </div>

            {/* Scraping Credentials */}
            <div className="border-t border-blue-700 pt-4 mt-4">
              <h4 className="text-sm font-medium text-blue-200 mb-3">
                Scraping Credentials (Optional)
              </h4>
              <p className="text-xs text-blue-300 mb-3">
                If your course pages require login, provide the email and password used to access the Customer Center. These are used for scraping lesson content — not for the ClickFunnels API.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Login Email
                  </label>
                  <input
                    type="email"
                    value={formData.scrape_email}
                    onChange={(e) => setFormData({ ...formData, scrape_email: e.target.value })}
                    placeholder="your-login@example.com"
                    className="w-full px-3 py-2 border border-blue-700 rounded-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">
                    Login Password
                  </label>
                  <input
                    type="password"
                    value={formData.scrape_password}
                    onChange={(e) => setFormData({ ...formData, scrape_password: e.target.value })}
                    placeholder={editingId ? 'Leave blank to keep current' : 'Enter login password'}
                    className="w-full px-3 py-2 border border-blue-700 rounded-lg bg-blue-950 text-white placeholder-blue-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-blue-200 bg-blue-800 rounded-lg hover:bg-blue-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-white text-blue-900 font-medium rounded-lg hover:bg-blue-50 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connections List */}
      <div className="space-y-3">
        {integrations.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No course platform connections yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Connect ClickFunnels to start importing courses and lessons
            </p>
          </div>
        ) : (
          integrations.map((integration) => {
            const results = testResults[integration.id] || [];
            const apiResult = results.find(r => r.type === 'api');
            const credResult = results.find(r => r.type === 'credentials');
            const isExpanded = expandedId === integration.id;
            const hasCredentials = !!integration.scrape_email;
            const isTestingThis = testingId === integration.id;

            return (
              <div
                key={integration.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Main Row */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-2 rounded-full ${
                        integration.is_connected
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {integration.is_connected ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{integration.name}</h4>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                          ClickFunnels
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {integration.workspace}.myclickfunnels.com
                      </p>
                      {integration.last_tested_at && (
                        <p className="text-xs text-gray-400">
                          Last tested: {new Date(integration.last_tested_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Test API Button */}
                    <button
                      onClick={() => handleTestApi(integration.id)}
                      disabled={isTestingThis}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
                        apiResult
                          ? apiResult.success
                            ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                            : 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                          : 'border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                      title="Test API connection"
                    >
                      {isTestingThis && testingType === 'api' ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : apiResult?.success ? (
                        <CheckCircle className="w-3.5 h-3.5" />
                      ) : apiResult ? (
                        <XCircle className="w-3.5 h-3.5" />
                      ) : (
                        <Wifi className="w-3.5 h-3.5" />
                      )}
                      API
                    </button>

                    {/* Test Credentials Button */}
                    <button
                      onClick={() => handleTestCredentials(integration.id)}
                      disabled={isTestingThis || !hasCredentials}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
                        !hasCredentials
                          ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                          : credResult
                            ? credResult.success
                              ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                              : 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                            : 'border-gray-300 text-gray-600 hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                      title={hasCredentials ? 'Test login credentials' : 'No scrape credentials set'}
                    >
                      {isTestingThis && testingType === 'credentials' ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : credResult?.success ? (
                        <CheckCircle className="w-3.5 h-3.5" />
                      ) : credResult ? (
                        <XCircle className="w-3.5 h-3.5" />
                      ) : (
                        <KeyRound className="w-3.5 h-3.5" />
                      )}
                      Login
                    </button>

                    {/* Expand/Collapse */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : integration.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    <button
                      onClick={() => startEdit(integration)}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(integration.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                      title="Delete connection"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
                    {/* Connection Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Workspace:</span>{' '}
                        <span className="text-gray-900">{integration.workspace}.myclickfunnels.com</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Customer Center:</span>{' '}
                        <span className="text-gray-900">{integration.customer_center_url || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Scrape Email:</span>{' '}
                        <span className="text-gray-900">{integration.scrape_email || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Scrape Password:</span>{' '}
                        <span className="text-gray-900">{integration.scrape_email ? '********' : 'Not set'}</span>
                      </div>
                    </div>

                    {/* Test Results */}
                    {(apiResult || credResult) && (
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Test Results</h5>

                        {apiResult && (
                          <div className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                            apiResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            {apiResult.success ? (
                              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <span className="font-medium">API Connection:</span>{' '}
                              {apiResult.success ? 'Connected successfully' : apiResult.error}
                            </div>
                          </div>
                        )}

                        {credResult && (
                          <div className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                            credResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            {credResult.success ? (
                              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <span className="font-medium">Login Test:</span>{' '}
                              {credResult.success
                                ? `Login successful (redirected to ${credResult.details?.postLoginUrl || 'dashboard'})`
                                : credResult.error}
                              {credResult.details && (
                                <div className="mt-1 text-xs space-y-0.5 opacity-80">
                                  {credResult.details.loginUrl && (
                                    <div>Login URL: {credResult.details.loginUrl}</div>
                                  )}
                                  {credResult.details.initialUrl && (
                                    <div>Initial URL (after redirects): {credResult.details.initialUrl}</div>
                                  )}
                                  <div>Login page loaded: {credResult.details.loginPageLoaded ? 'Yes' : 'No'}</div>
                                  <div>Password field visible: {credResult.details.passwordFieldVisible ? 'Yes' : 'No'}</div>
                                  <div>Form found: {credResult.details.formFound ? 'Yes' : 'No'}</div>
                                  {credResult.details.filledEmail && (
                                    <div>Filled email: {credResult.details.filledEmail}</div>
                                  )}
                                  {(credResult.details.filledPasswordLength ?? 0) > 0 && (
                                    <div>Password filled: {credResult.details.filledPasswordLength} chars</div>
                                  )}
                                  <div>Login submitted: {credResult.details.loginSubmitted ? 'Yes' : 'No'}</div>
                                  {credResult.details.postLoginUrl && (
                                    <div>Post-login URL: {credResult.details.postLoginUrl}</div>
                                  )}
                                  {credResult.details.postLoginTitle && (
                                    <div>Post-login page title: {credResult.details.postLoginTitle}</div>
                                  )}
                                  {credResult.details.pageErrors && credResult.details.pageErrors.length > 0 && (
                                    <div className="mt-1 text-red-600">
                                      {credResult.details.pageErrors.map((err, i) => (
                                        <div key={i}>{err}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No credentials warning */}
                    {!hasCredentials && (
                      <div className="flex items-center gap-2 p-2 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                        <HelpCircle className="w-4 h-4 flex-shrink-0" />
                        <span>No scrape credentials set. Edit this connection to add login email/password for scraping protected course pages.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
